import React from 'react';
import { Link } from 'react-router-dom';
import { Vote, BarChart, Users, DollarSign, MapPin } from 'lucide-react';

const OurReforms = () => {
  // Color palette from your existing app
  const colorPalette = {
    primary: '#922c40',   // Burgundy/wine red
    secondary: '#DC9750', // Bronze/gold
    tertiary: '#F3EAC0',  // Cream/beige
    light: '#F9F5E3',     // Lighter cream for backgrounds
    dark: '#1E2640',      // Navy/dark blue
    white: '#FFFFFF',
    black: '#333333'
  };

  return (
    <div style={{ backgroundColor: colorPalette.light }}>
      {/* Hero Section */}
      <section style={{
        backgroundColor: colorPalette.dark,
        color: 'white',
        padding: '80px 0 60px',
        textAlign: 'center'
      }}>
        <div className="container">
          <h1 style={{ 
            fontSize: '3rem',
            marginBottom: '16px',
            fontWeight: '800'
          }}>Our Reform Demands</h1>
          <p style={{
            fontSize: '1.25rem',
            maxWidth: '800px',
            margin: '0 auto 24px',
            lineHeight: '1.6',
            opacity: '0.9'
          }}>
            Comprehensive electoral reforms to build a democracy that truly represents all Americans.
          </p>
        </div>
      </section>

      {/* Context Section */}
      <section style={{
        padding: '60px 0',
        backgroundColor: 'white'
      }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: '2.2rem',
              color: colorPalette.primary,
              marginBottom: '20px',
              textAlign: 'center'
            }}>Why We Need Reform</h2>
            
            <p style={{ fontSize: '1.1rem', lineHeight: '1.6', marginBottom: '20px' }}>
              Our current electoral system is failing American voters. Power has been systematically shifted away from ordinary citizens
              and concentrated in the hands of politicians, special interests, and wealthy donors. The result is a democracy that doesn't 
              represent the people it's meant to serve.
            </p>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: window.innerWidth < 768 ? '1fr' : 'repeat(2, 1fr)',
              gap: '30px',
              marginTop: '40px',
              marginBottom: '40px'
            }}>
              {/* Problem Box 1 */}
              <div style={{
                backgroundColor: colorPalette.light,
                borderRadius: '8px',
                padding: '25px',
                boxShadow: '0 4px 10px rgba(0,0,0,0.05)',
                borderLeft: `4px solid ${colorPalette.primary}`
              }}>
                <h3 style={{ fontSize: '1.4rem', marginBottom: '15px', color: colorPalette.dark }}>
                  Rigged System
                </h3>
                <p style={{ fontSize: '1rem', lineHeight: '1.6' }}>
                  Politicians manipulate district boundaries through a process known as gerrymandering and maintain winner-takes-all voting systems 
                  that protect their power and shut out any voices and parties that go against them. This ensures they remain unaccountable while 
                  appearing to follow democratic processes.
                </p>
              </div>
              
              {/* Problem Box 2 */}
              <div style={{
                backgroundColor: colorPalette.light,
                borderRadius: '8px',
                padding: '25px',
                boxShadow: '0 4px 10px rgba(0,0,0,0.05)',
                borderLeft: `4px solid ${colorPalette.primary}`
              }}>
                <h3 style={{ fontSize: '1.4rem', marginBottom: '15px', color: colorPalette.dark }}>
                  Corrupting Influence of Dark Money
                </h3>
                <p style={{ fontSize: '1rem', lineHeight: '1.6' }}>
                  Foreign governments, corporations, and wealthy donors exert enormous influence through campaign financing, 
                  making politicians more accountable to their funders than to voters. This corruption undermines the 
                  foundational principle of a government "by and for the people".
                </p>
              </div>
              
              {/* Problem Box 3 */}
              <div style={{
                backgroundColor: colorPalette.light,
                borderRadius: '8px',
                padding: '25px',
                boxShadow: '0 4px 10px rgba(0,0,0,0.05)',
                borderLeft: `4px solid ${colorPalette.primary}`
              }}>
                <h3 style={{ fontSize: '1.4rem', marginBottom: '15px', color: colorPalette.dark }}>
                  Limited Voter Choice
                </h3>
                <p style={{ fontSize: '1rem', lineHeight: '1.6' }}>
                  The two-party duopoly forces voters to choose between "the lesser of two evils" rather than candidates who 
                  truly represent their values. Third parties and independents face nearly insurmountable structural barriers, 
                  silencing other ideologies in our political system.
                </p>
              </div>
              
              {/* Problem Box 4 */}
              <div style={{
                backgroundColor: colorPalette.light,
                borderRadius: '8px',
                padding: '25px',
                boxShadow: '0 4px 10px rgba(0,0,0,0.05)',
                borderLeft: `4px solid ${colorPalette.primary}`
              }}>
                <h3 style={{ fontSize: '1.4rem', marginBottom: '15px', color: colorPalette.dark }}>
                  Wasted Votes
                </h3>
                <p style={{ fontSize: '1rem', lineHeight: '1.6' }}>
                  In our current system, millions of votes effectively don't count. If you vote for a candidate who loses or 
                  for a dominant candidate in a safe district, your vote has no impact on representation. This discourages 
                  participation and leads to unrepresentative outcomes. Most people already feel this pain and don't vote, don't
                  feel like their vote matters, or don't even bother registering to vote and giving up on the system completely.
                </p>
              </div>
            </div>
            
            <div style={{
              backgroundColor: colorPalette.tertiary,
              padding: '30px',
              borderRadius: '8px',
              marginBottom: '40px',
              textAlign: 'center'
            }}>
              <h3 style={{ fontSize: '1.6rem', marginBottom: '15px', color: colorPalette.dark }}>
                Our Solution: Proportional Electoral Reform
              </h3>
              <p style={{ fontSize: '1.1rem', lineHeight: '1.6' }}>
                We call for a comprehensive series of electoral reforms on the state and national levels, 
                collectively referred to as "Proportional Electoral Reform (PER)." These reforms will 
                fundamentally transform American democracy by returning power to the voters, 
                eliminating gerrymandering, reducing the influence of money in politics, and enabling a 
                multi-party democracy that truly represents the diversity of American opinion.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Demands Section */}
      <section style={{
        padding: '60px 0',
        backgroundColor: colorPalette.light
      }}>
        <div className="container">
          <h2 style={{
            fontSize: '2.2rem',
            color: colorPalette.dark,
            marginBottom: '40px',
            textAlign: 'center'
          }}>Our Five Key Demands</h2>
          
          {/* Reform 1 */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '30px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
            marginBottom: '30px',
            display: 'flex',
            flexDirection: window.innerWidth < 768 ? 'column' : 'row',
            alignItems: 'flex-start'
          }}>
            <div style={{
              backgroundColor: colorPalette.primary,
              color: 'white',
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '25px',
              marginBottom: window.innerWidth < 768 ? '20px' : '0',
              flexShrink: 0
            }}>
              <MapPin size={30} />
            </div>
            
            <div>
              <h3 style={{ fontSize: '1.6rem', marginBottom: '15px', color: colorPalette.primary }}>
                1. Restructure State and Federal Houses of Representatives
              </h3>
              
              <p style={{ fontSize: '1.1rem', lineHeight: '1.6', marginBottom: '20px' }}>
                The restructuring of state and federal Houses of Representatives into proportionally elected chambers comprised 
                of officials from multi-member electoral districts; i.e., the repeal and/or amendment of the Uniform Congressional 
                District Act (UCDA) and passage of the Fair Representation Act (FRA).
              </p>
              
              <div style={{
                backgroundColor: `${colorPalette.light}`,
                padding: '20px',
                borderRadius: '8px',
                marginTop: '20px'
              }}>
                <h4 style={{ fontSize: '1.2rem', marginBottom: '10px', color: colorPalette.dark }}>
                  Why This Matters:
                </h4>
                <p style={{ fontSize: '1rem', lineHeight: '1.6' }}>
                  Multi-member districts eliminate gerrymandering and ensure that the percentage of votes a party receives is
                  about the percentage of seats they receive. This change is fundamental to creating space for new political voices 
                  that can effectively represent working people's interests.
                </p>
              </div>
            </div>
          </div>
          
          {/* Reform 2 */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '30px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
            marginBottom: '30px',
            display: 'flex',
            flexDirection: window.innerWidth < 768 ? 'column' : 'row',
            alignItems: 'flex-start'
          }}>
            <div style={{
              backgroundColor: colorPalette.primary,
              color: 'white',
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '25px',
              marginBottom: window.innerWidth < 768 ? '20px' : '0',
              flexShrink: 0
            }}>
              <BarChart size={30} />
            </div>
            
            <div>
              <h3 style={{ fontSize: '1.6rem', marginBottom: '15px', color: colorPalette.primary }}>
                2. Open-List Proportional Representation
              </h3>
              
              <p style={{ fontSize: '1.1rem', lineHeight: '1.6', marginBottom: '20px' }}>
                Implementation of an open-list proportional representation voting system on the federal, state, 
                and municipal (exceeding five hundred thousand persons) levels, where voters select their first and second 
                choice of parties (or independents, listed with/as parties), then rank candidates from said parties' lists 
                in order of preference, with the overall highest ranked officials occupying their party's seats.
              </p>
              
              <div style={{
                backgroundColor: `${colorPalette.light}`,
                padding: '20px',
                borderRadius: '8px',
                marginTop: '20px'
              }}>
                <h4 style={{ fontSize: '1.2rem', marginBottom: '10px', color: colorPalette.dark }}>
                  Why This Matters:
                </h4>
                <p style={{ fontSize: '1rem', lineHeight: '1.6' }}>
                  Open-list proportional representation ensures that voters have maximum choice and power. It allows voters to 
                  support parties that align with their values while also selecting specific candidates they trust. This system 
                  effectively ends "wasted votes" and ensures every party receives representation proportional to their numbers.
                </p>
              </div>
            </div>
          </div>
          
          {/* Reform 3 */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '30px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
            marginBottom: '30px',
            display: 'flex',
            flexDirection: window.innerWidth < 768 ? 'column' : 'row',
            alignItems: 'flex-start'
          }}>
            <div style={{
              backgroundColor: colorPalette.primary,
              color: 'white',
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '25px',
              marginBottom: window.innerWidth < 768 ? '20px' : '0',
              flexShrink: 0
            }}>
              <Vote size={30} />
            </div>
            
            <div>
              <h3 style={{ fontSize: '1.6rem', marginBottom: '15px', color: colorPalette.primary }}>
                3. Single-Transferable Voting
              </h3>
              
              <p style={{ fontSize: '1.1rem', lineHeight: '1.6', marginBottom: '20px' }}>
                The implementation of single-transferable voting (STV) in municipalities below five hundred thousand persons.
              </p>
              
              <div style={{
                backgroundColor: `${colorPalette.light}`,
                padding: '20px',
                borderRadius: '8px',
                marginTop: '20px'
              }}>
                <h4 style={{ fontSize: '1.2rem', marginBottom: '10px', color: colorPalette.dark }}>
                  Why This Matters:
                </h4>
                <p style={{ fontSize: '1rem', lineHeight: '1.6' }}>
                  STV is ideal for smaller municipalities because it allows voters to rank candidates in order of preference, 
                  ensuring that votes are not wasted if a voter's preferred candidate cannot win. This system gives voters more 
                  choice and leads to elected bodies that better reflect the diversity of the community, even in smaller settings.
                  It also avoids voting in multiple rounds, because you can run primaries during the actual election itself and since 
                  the votes transfer, there is never a need to come back and vote again for a runoff.
                </p>
              </div>
            </div>
          </div>
          
          {/* Reform 4 */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '30px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
            marginBottom: '30px',
            display: 'flex',
            flexDirection: window.innerWidth < 768 ? 'column' : 'row',
            alignItems: 'flex-start'
          }}>
            <div style={{
              backgroundColor: colorPalette.primary,
              color: 'white',
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '25px',
              marginBottom: window.innerWidth < 768 ? '20px' : '0',
              flexShrink: 0
            }}>
              <DollarSign size={30} />
            </div>
            
            <div>
              <h3 style={{ fontSize: '1.6rem', marginBottom: '15px', color: colorPalette.primary }}>
                4. Public Campaign Financing
              </h3>
              
              <p style={{ fontSize: '1.1rem', lineHeight: '1.6', marginBottom: '20px' }}>
                Abolition of private campaign financing in favor of public funding for all electoral campaigns; 
                i.e., the overturning of Citizens United v. FEC.
              </p>
              
              <div style={{
                backgroundColor: `${colorPalette.light}`,
                padding: '20px',
                borderRadius: '8px',
                marginTop: '20px'
              }}>
                <h4 style={{ fontSize: '1.2rem', marginBottom: '10px', color: colorPalette.dark }}>
                  Why This Matters:
                </h4>
                <p style={{ fontSize: '1rem', lineHeight: '1.6' }}>
                  Public campaign financing breaks the corrupting influence of money in politics. It ensures that politicians 
                  are accountable to the votes who are financing them, not wealthy donors, corporations, or foreign governments. 
                  This reform is essential for restoring trust in government and ensuring that elected officials work for the people 
                  they represent rather than those who fund their campaigns.
                </p>
              </div>
            </div>
          </div>
          
          {/* Reform 5 */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '30px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
            marginBottom: '30px',
            display: 'flex',
            flexDirection: window.innerWidth < 768 ? 'column' : 'row',
            alignItems: 'flex-start'
          }}>
            <div style={{
              backgroundColor: colorPalette.primary,
              color: 'white',
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '25px',
              marginBottom: window.innerWidth < 768 ? '20px' : '0',
              flexShrink: 0
            }}>
              <Users size={30} />
            </div>
            
            <div>
              <h3 style={{ fontSize: '1.6rem', marginBottom: '15px', color: colorPalette.primary }}>
                5. Direct Popular Vote for Executives
              </h3>
              
              <p style={{ fontSize: '1.1rem', lineHeight: '1.6', marginBottom: '20px' }}>
                Election of executives through direct popular vote, i.e., the marginalization of the Electoral College via 
                the National Popular Vote Interstate Compact (NPVIC).
              </p>
              
              <div style={{
                backgroundColor: `${colorPalette.light}`,
                padding: '20px',
                borderRadius: '8px',
                marginTop: '20px'
              }}>
                <h4 style={{ fontSize: '1.2rem', marginBottom: '10px', color: colorPalette.dark }}>
                  Why This Matters:
                </h4>
                <p style={{ fontSize: '1rem', lineHeight: '1.6' }}>
                  The Electoral College is an anti-democratic institution that has repeatedly awarded the presidency to 
                  candidates who lost the popular vote. Direct election of executives via STV ensures that every vote counts equally, 
                  regardless of whether you live in a swing state or not. This reform is vital for ensuring that executive offices truly 
                  represent the will of the people.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Transformative Impact Section */}
      <section style={{
        padding: '60px 0',
        backgroundColor: 'white'
      }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: '2.2rem',
              color: colorPalette.primary,
              marginBottom: '30px',
              textAlign: 'center'
            }}>The Transformative Impact of Our Reforms</h2>
            
            <div style={{
              backgroundColor: colorPalette.light,
              borderRadius: '8px',
              padding: '30px',
              boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
              marginBottom: '30px'
            }}>
              <h3 style={{ fontSize: '1.4rem', marginBottom: '20px', color: colorPalette.dark }}>Together, these reforms will:</h3>
              
              <ul style={{ padding: '0 0 0 20px', margin: '0' }}>
                <li style={{ fontSize: '1.1rem', marginBottom: '15px' }}>
                  <strong>Give You Choices</strong> - Create space for multiple parties to meaningfully participate in government
                </li>
                <li style={{ fontSize: '1.1rem', marginBottom: '15px' }}>
                  <strong>End gerrymandering</strong> - Make district manipulation impossible through multi-member districts with proportional representation
                </li>
                <li style={{ fontSize: '1.1rem', marginBottom: '15px' }}>
                  <strong>Reduce polarization</strong> - Giving voters choices forces parties to work together and encourages coalition-building and compromise instead of zero-sum partisan gridlock
                </li>
                <li style={{ fontSize: '1.1rem', marginBottom: '15px' }}>
                  <strong>Eliminate wasted votes</strong> - Ensure that nearly every voter helps elect someone who shares their values
                </li>
                <li style={{ fontSize: '1.1rem', marginBottom: '15px' }}>
                  <strong>Combat corruption</strong> - Make politicians accountable to voters, not donors, by removing private money from politics
                </li>
                <li style={{ fontSize: '1.1rem', marginBottom: '15px' }}>
                  <strong>Increase voter turnout</strong> - Give people a reason to vote by ensuring their vote actually matters and like they have a say in politics
                </li>
                <li style={{ fontSize: '1.1rem' }}>
                  <strong>Return power to the people</strong> - Create a democracy that truly represents all Americans, not just powerful special interests
                </li>
              </ul>
            </div>
            
            {/* What PER Won't Do Section */}
            <div style={{
              backgroundColor: colorPalette.tertiary,
              borderRadius: '8px',
              padding: '30px',
              boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
              marginBottom: '30px',
              border: `1px solid ${colorPalette.secondary}`
            }}>
              <h3 style={{ 
                fontSize: '1.6rem', 
                marginBottom: '20px', 
                color: colorPalette.dark,
                textAlign: 'center'
              }}>Understanding the Limits of Electoral Reform</h3>
              
              <p style={{ fontSize: '1.1rem', lineHeight: '1.6', marginBottom: '20px' }}>
                While Proportional Electoral Reform is essential for creating a more democratic system, we recognize that 
                it is not a silver bullet that will solve all of America's problems overnight. It's important to understand 
                what these reforms will and won't do:
              </p>
              
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: window.innerWidth < 768 ? '1fr' : 'repeat(2, 1fr)',
                gap: '20px',
                marginBottom: '20px'
              }}>
                <div style={{
                  backgroundColor: 'white',
                  padding: '20px',
                  borderRadius: '8px'
                }}>
                  <h4 style={{ fontSize: '1.2rem', marginBottom: '10px', color: colorPalette.primary }}>
                    Electoral Reform Will Not:
                  </h4>
                  <ul style={{ padding: '0 0 0 20px', margin: '0' }}>
                    <li style={{ fontSize: '1rem', marginBottom: '10px' }}>
                      <strong>Automatically create new political parties</strong> - People will still need to organize, 
                      build, and support alternative parties
                    </li>
                    <li style={{ fontSize: '1rem', marginBottom: '10px' }}>
                      <strong>Solve deep-rooted societal problems immediately</strong> - Many issues stem from decades 
                      of neglect and misunderstanding and will require sustained effort to address
                    </li>
                    <li style={{ fontSize: '1rem', marginBottom: '10px' }}>
                      <strong>Dictate which ideologies succeed</strong> - Our goal is to create fair space for 
                      competition, not to predetermine outcomes like the current system does
                    </li>
                    <li style={{ fontSize: '1rem' }}>
                      <strong>Replace the need for ongoing civic engagement</strong> - Democracy will always require constant 
                      participation, not just better rules. The greedy will always try to take advantage of any system and bend the
                      rules to their advantage. It is our duty as citizens to prevent that from happening.
                    </li>
                  </ul>
                </div>
                
                <div style={{
                  backgroundColor: 'white',
                  padding: '20px',
                  borderRadius: '8px'
                }}>
                  <h4 style={{ fontSize: '1.2rem', marginBottom: '10px', color: colorPalette.primary }}>
                    Electoral Reform Will:
                  </h4>
                  <ul style={{ padding: '0 0 0 20px', margin: '0' }}>
                    <li style={{ fontSize: '1rem', marginBottom: '10px' }}>
                      <strong>Remove structural barriers</strong> - Create the conditions where new parties and 
                      movements can grow and succeed
                    </li>
                    <li style={{ fontSize: '1rem', marginBottom: '10px' }}>
                      <strong>Expand our political imagination</strong> - Allow for solutions we can't yet envision 
                      under the current system - like nation-wide high-speed rail or an improved immigration process.
                    </li>
                    <li style={{ fontSize: '1rem', marginBottom: '10px' }}>
                      <strong>Create space for innovation</strong> - Enable us to address and adapt to pressing issues, 
                      like climate change, with fresh approaches
                    </li>
                    <li style={{ fontSize: '1rem' }}>
                      <strong>Facilitate democratic evolution</strong> - Allow our political system to adapt to rapidly 
                      changing needs and challenges. If a party ever stops meeting the needs of the people, you can easily
                      create a new one, and the system will allow it the space to grow and survive if you gain a reasonable
                      amount of support.
                    </li>
                  </ul>
                </div>
              </div>
              
              <p style={{ 
                fontSize: '1.1rem', 
                lineHeight: '1.6', 
                fontStyle: 'italic',
                backgroundColor: 'rgba(255,255,255,0.5)',
                padding: '15px',
                borderRadius: '6px'
              }}>
                We are focused on creating the fertile ground where a more vibrant democracy can grow. 
                What actually grows there—which parties emerge, what policies they champion, how they govern—will 
                ultimately be determined by the American people through a truly democratic process. We aim to 
                build and reform the structures and systems that currently hold us back, opening the way for truly
                democratic solutions.
              </p>
            </div>
            
            <div style={{
              backgroundColor: colorPalette.primary,
              color: 'white',
              padding: '30px',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <h3 style={{ fontSize: '1.6rem', marginBottom: '15px' }}>
                Join Us in Transforming American Democracy
              </h3>
              <p style={{ fontSize: '1.1rem', lineHeight: '1.6', marginBottom: '25px' }}>
                These reforms aren't just good ideas—they're essential first steps toward creating a democracy that works for everyone. 
                Together, we can build a movement that puts power back where it belongs: with the people.
              </p>
              <Link to="/get-involved" style={{
                backgroundColor: 'white',
                color: colorPalette.primary,
                padding: '12px 25px',
                borderRadius: '6px',
                fontWeight: '600',
                textDecoration: 'none',
                display: 'inline-block',
                fontSize: '1.1rem'
              }}>
                Get Involved
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OurReforms;